import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { DateRangeField } from '../components/DateRangeField';
import { getErrorMessage } from '../api/client';
import { getLoyverseCatalogStores } from '../api/loyverse';
import { getDailySalesSummary, getReceipt, getReceipts } from '../api/receipts';
import { getStores } from '../api/stores';
import { useAuth } from '../auth/AuthProvider';
import { getStoreScopeOptions } from '../auth/permissions';
import { Modal } from '../components/Modal';
import { DEFAULT_STORE_TIMEZONE, resolveStoreTimezone } from '../utils/timezones';
import { useLoyversePresence } from '../utils/loyversePresence';
import type { LoyverseCatalogStore } from '../types/loyverse';
import type {
  DailySalesSummary,
  ReceiptDetail,
  ReceiptFilters,
  ReceiptListItem,
} from '../types/receipts';

const PAGE_SIZE = 20;

interface FilterState {
  dateFrom: string;
  dateTo: string;
  storeExternalId: string;
  search: string;
}

export function SalesReceiptsPage() {
  const { user } = useAuth();
  const loyverseConnected = useLoyversePresence();
  const initialRange = createRange(7);
  const [draftFilters, setDraftFilters] = useState<FilterState>({
    ...initialRange,
    storeExternalId: '',
    search: '',
  });
  const [filters, setFilters] = useState<FilterState>({
    ...initialRange,
    storeExternalId: '',
    search: '',
  });
  const [items, setItems] = useState<ReceiptListItem[]>([]);
  const [summary, setSummary] = useState<DailySalesSummary>();
  const [stores, setStores] = useState<LoyverseCatalogStore[]>([]);
  const [assignedStoreExternalIds, setAssignedStoreExternalIds] = useState<string[]>();
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [detail, setDetail] = useState<ReceiptDetail | null>(null);
  const [detailError, setDetailError] = useState<string>();
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isStoreScopeReady, setIsStoreScopeReady] = useState(false);

  useEffect(() => {
    void getStores().then((result) => {
      setAssignedStoreExternalIds(result.stores.map((store) => store.externalId));
      const { defaultStoreId } = getStoreScopeOptions(user?.role, result.stores);
      const defaultExternalId = result.stores.find((store) => store.id === defaultStoreId)?.externalId;
      if (defaultExternalId) {
        setDraftFilters((current) => ({ ...current, storeExternalId: defaultExternalId }));
        setFilters((current) => ({ ...current, storeExternalId: defaultExternalId }));
      }
    }).catch((loadError) => setError(getErrorMessage(loadError, 'No fue posible cargar las tiendas asignadas.'))).finally(() => setIsStoreScopeReady(true));
  }, [user?.role]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);

    const requestFilters: ReceiptFilters = {
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      limit: PAGE_SIZE,
      offset,
      ...(filters.storeExternalId
        ? { storeExternalId: filters.storeExternalId }
        : {}),
      ...(filters.search ? { search: filters.search } : {}),
    };

    try {
      const [listResult, summaryResult, storesResult] = await Promise.all([
        getReceipts(requestFilters),
        getDailySalesSummary(requestFilters),
        getLoyverseCatalogStores(),
      ]);
      setItems(listResult.items);
      setTotal(listResult.total);
      setHasMore(listResult.hasMore);
      setSummary(summaryResult.summary);
      setStores(storesResult.stores);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'No fue posible cargar las ventas. Intenta de nuevo.'));
    } finally {
      setIsLoading(false);
    }
  }, [filters, offset]);

  useEffect(() => {
    if (!isStoreScopeReady) return;
    void loadData();
  }, [loadData, isStoreScopeReady]);

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    if (!draftFilters.dateFrom || !draftFilters.dateTo) {
      setError('Selecciona las fechas desde y hasta.');
      return;
    }
    if (draftFilters.dateFrom > draftFilters.dateTo) {
      setError('La fecha inicial no puede ser posterior a la fecha final.');
      return;
    }
    setOffset(0);
    setFilters({
      ...draftFilters,
      search: draftFilters.search.trim(),
    });
  };

  const openDetail = async (receiptId: string) => {
    setDetail(null);
    setDetailError(undefined);
    setIsDetailLoading(true);

    try {
      const result = await getReceipt(receiptId);
      setDetail(result.receipt);
    } catch (detailLoadError) {
      setDetailError(
        getErrorMessage(detailLoadError, 'No fue posible cargar el ticket.'),
      );
    } finally {
      setIsDetailLoading(false);
    }
  };

  const closeDetail = () => {
    if (isDetailLoading) return;
    setDetail(null);
    setDetailError(undefined);
  };

  return (
    <div className="admin-page sales-page">
      <header className="page-heading">
        <div>
          <span className="eyebrow">Resumen de ventas</span>
          <h1>Ventas Loyverse</h1>
          <p>Tickets sincronizados desde Loyverse.</p>
        </div>
        {/* ZTV-04: la CTA de sincronizar solo se ofrece cuando la integración existe. */}
        {loyverseConnected !== false && <button className="button button--primary" onClick={() => { window.location.hash = 'integrations'; }} type="button">Actualizar ventas desde Loyverse</button>}
      </header>

      {loyverseConnected === false && <div className="alert alert--info">
        Este módulo muestra las ventas conectadas desde la integración Loyverse, que tu negocio no tiene conectada.
        Es opcional: puedes seguir registrando tus ventas en <strong>Ventas e ingresos</strong>. Si usas Loyverse, conéctalo en Configuración → Integraciones.
      </div>}

      {error && <div className="alert alert--error">{error}</div>}

      <form className="admin-card sales-filter-card" onSubmit={applyFilters}>
        <div className="sales-filter-grid">
          <DateRangeField allowClear={false} from={draftFilters.dateFrom} maxToday onChange={(from, to) => setDraftFilters({ ...draftFilters, dateFrom: from, dateTo: to })} to={draftFilters.dateTo} />
          <label className="field"><span>Tienda</span><select onChange={(event) => setDraftFilters({ ...draftFilters, storeExternalId: event.target.value })} value={draftFilters.storeExternalId}>{user?.role === 'OWNER' && <option value="">Todas las tiendas</option>}{stores.filter((store) => user?.role === 'OWNER' || assignedStoreExternalIds?.includes(store.externalId)).map((store) => <option key={store.id} value={store.externalId}>{store.name}</option>)}</select></label>
          <label className="field"><span>Buscar</span><input maxLength={100} onChange={(event) => setDraftFilters({ ...draftFilters, search: event.target.value })} placeholder="Número de ticket o empleado" value={draftFilters.search} /></label>
        </div>
        {/* Ola 3 (D8): los atajos de fecha viven DENTRO del calendario del rango; aquí solo Aplicar. */}
        <div className="sales-filter-actions">
          <button className="button button--secondary" disabled={isLoading} type="submit">{isLoading ? 'Consultando...' : 'Aplicar filtros'}</button>
        </div>
      </form>

      <section className="sales-summary-grid">
        <SummaryCard label="Tickets" value={String(summary?.totals.receiptsCount ?? 0)} />
        <SummaryCard label="Venta total" value={formatMoney(summary?.totals.grossSales)} />
        <SummaryCard label="Pagado" value={formatMoney(summary?.totals.totalPaid)} />
        <SummaryCard label="Descuentos" value={formatMoney(summary?.totals.totalDiscount)} />
        <SummaryCard label="Impuestos" value={formatMoney(summary?.totals.totalTax)} />
        <SummaryCard label="Ticket promedio" value={formatMoney(summary?.totals.averageTicket)} />
      </section>

      {summary && (
        <>
          <div className="sales-sync-note">
            <span className={`status-pill ${summary.syncStatus === 'SUCCESS' ? '' : 'status-pill--neutral'}`}>{syncStatusLabel(summary.syncStatus)}</span>
            <span>Última sincronización: {formatDateTime(summary.lastSyncedAt)}</span>
          </div>
          <section className="sales-breakdown-grid">
            <SummaryTable title="Ventas por día" columns={['Fecha', 'Tickets', 'Venta', 'Pagado', 'Promedio']} empty="No hay ventas por día.">
              {summary.salesByDay.map((day) => <tr key={day.date}><td>{formatDay(day.date)}</td><td>{day.receiptsCount}</td><td>{formatMoney(day.grossSales)}</td><td>{formatMoney(day.totalPaid)}</td><td>{formatMoney(day.averageTicket)}</td></tr>)}
            </SummaryTable>
            <SummaryTable title="Ventas por tienda" columns={['Tienda', 'Tickets', 'Venta', 'Pagado', 'Promedio']} empty="No hay ventas por tienda.">
              {summary.salesByStore.map((store) => <tr key={store.storeId || store.storeName}><td>{store.storeName}</td><td>{store.receiptsCount}</td><td>{formatMoney(store.grossSales)}</td><td>{formatMoney(store.totalPaid)}</td><td>{formatMoney(store.averageTicket)}</td></tr>)}
            </SummaryTable>
            <SummaryTable title="Métodos de pago" columns={['Método', 'Operaciones', 'Importe']} empty="No hay pagos para este rango.">
              {summary.paymentsByType.map((payment) => <tr key={payment.paymentType}><td>{payment.paymentType}</td><td>{payment.count}</td><td>{formatMoney(payment.amount)}</td></tr>)}
            </SummaryTable>
            <SummaryTable title="Productos más vendidos" columns={['Producto', 'Cantidad', 'Tickets', 'Venta']} empty="No hay productos para este rango.">
              {summary.topItems.map((item) => <tr key={`${item.itemName}-${item.variantName || ''}`}><td><strong>{item.itemName}</strong>{item.variantName && <small>{item.variantName}</small>}</td><td>{formatQuantity(item.quantity)}</td><td>{item.receiptsCount}</td><td>{formatMoney(item.grossSales)}</td></tr>)}
            </SummaryTable>
          </section>
        </>
      )}

      <section className="admin-card sales-list-card">
        <div className="admin-card__summary">
          <span><strong>{total}</strong> tickets encontrados</span>
          <span>Mostrando {total ? offset + 1 : 0}–{Math.min(offset + items.length, total)}</span>
        </div>

        {isLoading ? (
          <div className="inline-loading"><span className="spinner" />Cargando ventas...</div>
        ) : items.length === 0 ? (
          <div className="empty-panel"><strong>Todavía no hay ventas en este rango de fechas</strong><span>Selecciona otro rango o actualiza ventas desde Loyverse.</span></div>
        ) : (
          <div className="data-table-wrap">
            {/* Móvil (≤640px): cada ticket se muestra como tarjeta (data-table--cards). */}
            <table className="data-table sales-table data-table--cards">
              <thead><tr><th>Fecha</th><th>Ticket</th><th>Tienda</th><th>Empleado</th><th>Total</th><th>Pago</th><th>Estado</th><th>Acción</th></tr></thead>
              <tbody>{items.map((item) => <ReceiptRow item={item} key={item.id} onOpen={openDetail} stores={stores} />)}</tbody>
            </table>
          </div>
        )}

        <div className="sales-pagination">
          <button className="button button--secondary" disabled={isLoading || offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} type="button">Anterior</button>
          <span>Página {Math.floor(offset / PAGE_SIZE) + 1}</span>
          <button className="button button--secondary" disabled={isLoading || !hasMore} onClick={() => setOffset(offset + PAGE_SIZE)} type="button">Siguiente</button>
        </div>
      </section>

      {(isDetailLoading || detailError || detail) && (
        <Modal
          description={detail ? formatDateTime(detail.receiptDate, resolveStoreTimezone(stores, { storeExternalId: detail.storeExternalId })) : 'Detalle del ticket'}
          isBusy={isDetailLoading}
          onClose={closeDetail}
          title={detail?.receiptNumber || 'Consultando ticket'}
        >
          {detailError && <div className="alert alert--error">{detailError}</div>}
          {isDetailLoading ? <div className="inline-loading"><span className="spinner" />Cargando ticket...</div> : detail && <ReceiptDetailView receipt={detail} stores={stores} />}
        </Modal>
      )}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return <article className="sales-summary-card"><span>{label}</span><strong>{value}</strong></article>;
}

function SummaryTable({
  title,
  columns,
  empty,
  children,
}: {
  title: string;
  columns: string[];
  empty: string;
  children: React.ReactNode;
}) {
  const hasRows = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return (
    <article className="admin-card sales-breakdown-card">
      <header><h2>{title}</h2></header>
      {hasRows ? (
        <div className="data-table-wrap"><table className="sales-summary-table"><thead><tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr></thead><tbody>{children}</tbody></table></div>
      ) : <p className="empty-state">{empty}</p>}
    </article>
  );
}

function ReceiptRow({ item, onOpen, stores }: { item: ReceiptListItem; onOpen: (id: string) => Promise<void>; stores: LoyverseCatalogStore[] }) {
  const cancelled = Boolean(item.cancelledAt);
  const timeZone = resolveStoreTimezone(stores, { storeExternalId: item.storeExternalId });
  // Acordeón móvil (mismo patrón que el historial de cortes): tarjeta colapsada = fecha + total +
  // estado + acción; tocarla expande solo esa. En escritorio no cambia nada.
  const [expanded, setExpanded] = useState(false);
  return (
    <tr className={`${cancelled ? 'data-row--inactive' : ''} ${expanded ? 'dt-card--open' : 'dt-card--closed'}`} onClick={() => setExpanded((value) => !value)}>
      <td className="dt-card-main"><strong>{formatDate(item.receiptDate, timeZone)}</strong><small>{formatTime(item.receiptDate, timeZone)}</small></td>
      <td data-label="Ticket"><strong>{item.receiptNumber || item.externalId}</strong><small>{item.linesCount} producto{item.linesCount === 1 ? '' : 's'}</small></td>
      <td data-label="Tienda">{item.storeName || item.storeExternalId || 'Sin tienda'}</td>
      <td data-label="Empleado">{item.employeeName || item.employeeExternalId || 'Sin empleado'}</td>
      <td className="dt-card-keep" data-label="Total"><strong>{formatMoney(item.totalMoney, item.currency)}</strong></td>
      <td data-label="Pago">{item.paymentSummary || 'Sin dato'}</td>
      <td className="dt-card-keep" data-label="Estado"><span className={`status-pill ${cancelled ? 'status-pill--inactive' : ''}`}>{cancelled ? 'Cancelado' : item.status || 'Completado'}</span></td>
      <td className="dt-card-actions dt-card-keep"><button className="text-button" onClick={(event) => { event.stopPropagation(); void onOpen(item.id); }} type="button">Ver detalle</button></td>
    </tr>
  );
}

function ReceiptDetailView({ receipt, stores }: { receipt: ReceiptDetail; stores: LoyverseCatalogStore[] }) {
  const timeZone = resolveStoreTimezone(stores, { storeExternalId: receipt.storeExternalId });
  return (
    <div className="receipt-detail">
      <dl className="detail-list">
        <div><dt>Tienda</dt><dd>{receipt.storeName || receipt.storeExternalId || 'Sin dato'}</dd></div>
        <div><dt>Empleado</dt><dd>{receipt.employeeName || receipt.employeeExternalId || 'Sin dato'}</dd></div>
        <div><dt>Venta total</dt><dd>{formatMoney(receipt.totalMoney, receipt.currency)}</dd></div>
        <div><dt>Pagado</dt><dd>{formatMoney(receipt.totalPaid, receipt.currency)}</dd></div>
        <div><dt>Descuento</dt><dd>{formatMoney(receipt.totalDiscount, receipt.currency)}</dd></div>
        <div><dt>Impuestos</dt><dd>{formatMoney(receipt.totalTax, receipt.currency)}</dd></div>
      </dl>

      <h3>Productos</h3>
      <div className="receipt-detail-list">
        {receipt.lines.map((line) => (
          <article key={line.id}>
            <div><strong>{line.itemName}</strong><small>{[line.variantName, line.sku].filter(Boolean).join(' · ') || 'Sin variante'}</small></div>
            <span>{formatQuantity(line.quantity)} × {formatMoney(line.unitPrice, receipt.currency)}</span>
            <strong>{formatMoney(line.totalAmount, receipt.currency)}</strong>
          </article>
        ))}
      </div>

      <h3>Pagos</h3>
      <div className="receipt-payment-list">
        {receipt.payments.length ? receipt.payments.map((payment) => (
          <article key={payment.id}><div><strong>{payment.paymentType}</strong><small>{formatDateTime(payment.paidAt, timeZone)}</small></div><strong>{formatMoney(payment.amount, payment.currency)}</strong></article>
        )) : <p className="empty-state">No hay pagos registrados.</p>}
      </div>
    </div>
  );
}

function createRange(days: number): Pick<FilterState, 'dateFrom' | 'dateTo'> {
  const dateTo = new Date();
  const dateFrom = new Date(dateTo);
  dateFrom.setDate(dateFrom.getDate() - Math.max(0, days - 1));
  return { dateFrom: toDateInput(dateFrom), dateTo: toDateInput(dateTo) };
}

function toDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatMoney(value?: string | null, currency = 'MXN'): string {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency || 'MXN' }).format(Number(value || 0));
}

function formatQuantity(value: string): string {
  return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 3 }).format(Number(value));
}

function formatDate(value: string | null, timeZone: string = DEFAULT_STORE_TIMEZONE): string {
  return value ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeZone }).format(new Date(value)) : 'Sin fecha';
}

function formatTime(value: string | null, timeZone: string = DEFAULT_STORE_TIMEZONE): string {
  return value ? new Intl.DateTimeFormat('es-MX', { timeStyle: 'short', timeZone }).format(new Date(value)) : '';
}

function formatDateTime(value: string | null, timeZone: string = DEFAULT_STORE_TIMEZONE): string {
  return value ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(value)) : 'Sin fecha';
}

function formatDay(value: string): string {
  return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00.000Z`));
}

function syncStatusLabel(status: string) {
  return { SUCCESS: 'Actualizado', RUNNING: 'Actualizando', FAILED: 'Con error', IDLE: 'Sin actualizar' }[status] || 'Sin información';
}
